import type { StaticEntityType } from "@config";
import { EMPTY_SIMPLE_COMBO } from "@/shared/data";

type ReturnType = {
  id: string;
  type: string;
  name?: string;
  payload?: StaticEntityType;
  parentId?: string;
};

const getActiveEntityPayload = ({
  element,
  id,
}: {
  element: StaticEntityType;
  id?: string;
}): ReturnType | null => {
  const selector = id ? `[data-layer-id="${id}"]` : ".--active[data-layer-id]";
  const entity = document.querySelector(
    `#content-sgKecQsAYKy7MX4 ${selector}`,
  ) as HTMLElement;
  if (!entity) return null;

  const { layerType: type } = entity.dataset;
  const elementType = element.type;

  if (elementType === "combo") {
    const slide = entity.closest("[data-layer-type='slide']") as HTMLElement;
    if (!slide) return null;
    return handleSlideActiveState(slide, element);
  }

  if (elementType === "element" && type === "combo") {
    return handleNewElementWithSimpleCombo(entity, element);
  }

  const mapped = {
    element: handleElementActiveState,
    set: handleSetActiveState,
    combo: handleComboActiveState,
    slide: handleSlideActiveState,
  };

  return mapped[type as keyof typeof mapped]?.(entity, element, id) || null;
};

export default getActiveEntityPayload;

/**
 * Handles the active state for element
 * @param entity - The HTML element representing the element
 * @param element - The element configuration object
 *
 * ELEMENT USE CASES:
 * 1. Active element - Create new element after last element
 */
const handleElementActiveState = (
  entity: HTMLElement,
  element: StaticEntityType,
  id?: string,
) => {
  const combo = entity.closest("[data-layer-type='combo']") as HTMLElement;
  const name = combo.dataset.comboName || "";

  if (name === "FullScreen") {
    return handleComboActiveState(combo, element, id, entity);
  }

  if (name === "Narrator") {
    const isMediaActive = !!entity.closest(".combo-narrator__media-container");
    if (isMediaActive) {
      const combo = entity.closest("[data-layer-type='combo']") as HTMLElement;
      return handleComboActiveState(combo, element);
    }
  }

  const group = entity.closest("[data-layer-type='group']") as HTMLElement;
  if (!group) return null;
  return {
    id: id || entity.dataset.layerId || "",
    type: "element",
    payload: element,
    parentId: group.dataset.layerId || "",
    name: entity.dataset.layerName || "",
  };
};

/**
 * Handles the active state for set
 * @param entity - The HTML element representing the set
 * @param element - The element configuration object
 *
 * SET USE CASES:
 * 1. Active set - Create new element after last element
 */
const handleSetActiveState = (
  entity: HTMLElement,
  element: StaticEntityType,
) => {
  const elements = entity.querySelectorAll(
    '[data-layer-type="element"]',
  ) as NodeListOf<HTMLElement>;
  const lastElement = elements[elements.length - 1];
  if (!lastElement) return null;
  const group = lastElement.closest("[data-layer-type='group']") as HTMLElement;
  if (!group) return null;
  return {
    id: lastElement.dataset.layerId || "",
    type: "element",
    payload: element,
    parentId: group.dataset.layerId || "",
    name: lastElement.dataset.layerName || "",
  };
};

/**
 * Handles the active state for combos
 * @param entity - The HTML element representing the combo
 * @param element - The element configuration object
 * @returns The payload object for empty combos, or null otherwise
 *
 * COMBO USE CASES:
 * 1. Empty combo - Create new element
 * 2. Non-empty combo - Create new combo after last combo
 */
const handleComboActiveState = (
  entity: HTMLElement,
  element: StaticEntityType,
  id?: string,
  target?: HTMLElement,
) => {
  // Case 1: Empty combo
  if (entity?.querySelector(".add-entity-placeholder-SxqseNspfN3v066")) {
    const group = entity.querySelector(
      '[data-layer-type="group"]',
    ) as HTMLElement;
    if (!group) return null;
    return {
      id: "",
      type: "element",
      payload: element,
      parentId: group.dataset.layerId || "",
    };
  }

  // Case 2: FullScreen combo
  if (entity.dataset.comboName === "FullScreen") {
    const mediaSide = target?.closest(
      '.pos-1[data-layer-type="group"]',
    ) as HTMLElement;
    let group;
    if (mediaSide) {
      const set = target?.closest('[data-layer-type="set"]') as HTMLElement;
      group = set.querySelector(
        '.pos-0[data-layer-type="group"]',
      ) as HTMLElement;
    } else {
      group = target?.closest('[data-layer-type="group"]') as HTMLElement;
    }
    if (!group) return null;
    const elements = group.querySelectorAll(
      '[data-layer-type="element"]',
    ) as NodeListOf<HTMLElement>;
    const lastElement = elements[elements.length - 1];
    if (!lastElement) return null;
    const payloadId = mediaSide
      ? (lastElement.dataset.layerId as string)
      : id || lastElement.dataset.layerId || "";
    return {
      id: payloadId,
      type: "element",
      payload: element,
      parentId: group.dataset.layerId || "",
      name: lastElement.dataset.layerName || "",
    };
  }

  // Case 3: Non-empty combo
  const set = entity.querySelector('[data-layer-type="set"]') as HTMLElement;
  if (!set) return null;
  const elements = set.querySelectorAll(
    '[data-layer-type="element"]',
  ) as NodeListOf<HTMLElement>;
  const lastElement = elements[elements.length - 1];
  if (!lastElement) return null;

  const group = lastElement.closest("[data-layer-type='group']") as HTMLElement;
  if (!group) return null;

  return {
    id: lastElement.dataset.layerId || "",
    type: "element",
    payload: element,
    parentId: group.dataset.layerId || "",
    name: lastElement.dataset.layerName || "",
  };
};

/**
 * Handles the active state for slides
 * @param entity - The HTML element representing the slide
 * @param element - The element configuration object
 * @returns The payload object for empty combos, or null otherwise
 *
 * SLIDE USE CASES:
 * 1. Empty slide - Create new combo
 * 2. Slide with empty combo - Add element to existing group
 * 3. Slide with non-empty combo - Create new combo after last combo
 */
const handleSlideActiveState = (
  entity: HTMLElement,
  element: StaticEntityType,
) => {
  const combos = entity.querySelectorAll('[data-layer-type="combo"]');
  const firstCombo = combos[0] as HTMLElement;

  const comboPayload =
    element.type === "combo"
      ? element
      : EMPTY_SIMPLE_COMBO(element as StaticEntityType);

  // Case 1: Empty slide
  if (!combos.length) {
    return {
      id: "",
      type: "combo",
      payload: comboPayload,
      parentId: entity.dataset.layerId as string,
    };
  }

  // Case 3: Empty combo
  if (firstCombo?.querySelector(".add-entity-placeholder-SxqseNspfN3v066")) {
    const group = firstCombo.querySelector(
      '[data-layer-type="group"]',
    ) as HTMLElement;
    if (!group) return null;
    if (element.type === "combo") {
      const lastCombo = combos[combos.length - 1] as HTMLElement;
      return {
        id: lastCombo.dataset.layerId || "",
        type: "combo",
        payload: comboPayload,
        parentId: entity.dataset.layerId as string,
      };
    }
    return {
      id: "",
      type: "element",
      payload: element,
      parentId: group.dataset.layerId || "",
    };
  }

  // Case 4: Non-empty combo
  const lastCombo = combos[combos.length - 1] as HTMLElement;
  return {
    id: lastCombo.dataset.layerId as string,
    type: "combo",
    payload: comboPayload,
    parentId: entity.dataset.layerId as string,
  };
};

const handleNewElementWithSimpleCombo = (
  comboEl: HTMLElement,
  element: StaticEntityType,
) => {
  const slide = comboEl.closest("[data-layer-type='slide']") as HTMLElement;
  if (!slide) return null;

  const comboPayload = EMPTY_SIMPLE_COMBO(element as StaticEntityType);
  return {
    id: comboEl.dataset.layerId as string,
    type: "combo",
    payload: comboPayload,
    parentId: slide.dataset.layerId as string,
  };
};
